import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useStytchB2BClient } from '@stytch/react/b2b';
import { ADMIN_ORG_SLUG } from '@/lib/stytch';
import { Button } from '@/components/ui/button';

/**
 * AuthCallbackPage — destino del magic link de Stytch.
 *
 * Flujo (discovery):
 *  1. `magicLinks.discovery.authenticate` con el token de la URL → lista
 *     de organizaciones a las que el usuario puede acceder + intermediate
 *     session token.
 *  2. Buscamos la organization del pool admin por slug.
 *  3. Intercambiamos el intermediate por una member session de esa org.
 *  4. Redirigimos a /admin.
 */
export default function AuthCallbackPage() {
  const stytch = useStytchB2BClient();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = params.get('token');
    if (!token) {
      setError('Falta el token del enlace.');
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const discovery = await stytch.magicLinks.discovery.authenticate({
          discovery_magic_links_token: token,
        });
        if (cancelled) return;

        const adminOrg = discovery.discovered_organizations.find(
          (o) => o.organization?.organization_slug === ADMIN_ORG_SLUG,
        );
        if (!adminOrg?.organization) {
          setError('No tienes acceso al pool admin de bailafm.');
          return;
        }

        await stytch.discovery.intermediateSessions.exchange({
          organization_id: adminOrg.organization.organization_id,
          session_duration_minutes: 60,
        });
        if (cancelled) return;

        navigate('/admin', { replace: true });
      } catch (e) {
        if (!cancelled) {
          setError(
            e instanceof Error ? e.message : 'No se ha podido completar el inicio de sesión.',
          );
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [params, stytch, navigate]);

  if (error) {
    return (
      <div className="w-full max-w-sm space-y-4 rounded-lg border bg-card p-8 shadow-sm text-center">
        <p className="text-sm text-destructive">{error}</p>
        <Button className="w-full" onClick={() => navigate('/login', { replace: true })}>
          Volver al login
        </Button>
      </div>
    );
  }

  return (
    <p className="text-sm text-muted-foreground">Iniciando sesión…</p>
  );
}
